import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, X, Check, RefreshCw, Wand2 } from "lucide-react";
import { api } from "@/lib/api";

const STYLES = [
  ["photo", "Photo"],
  ["illustration", "Illustration"],
  ["retro", "Retro Poster"],
];

export const AIImageModal = ({ open, onClose, onUse, context }) => {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("photo");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    if (context?.title) suggest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const suggest = async () => {
    setBusy("suggest");
    try {
      const r = await api.post("/mag/ai/image-prompt", {
        title: context?.title || "", subtitle: context?.subtitle || "", body: context?.body || "" });
      setPrompt(r.data.prompt || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't suggest a prompt"); }
    setBusy("");
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the image first"); return; }
    setBusy("generate");
    try {
      const r = await api.post("/mag/ai/image", { prompt, style, aspect_ratio: "landscape" });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Image generation failed"); }
    setBusy("");
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="mag-ai-image-modal">
      <div className="bg-bg-surface border border-line rounded-xl w-full max-w-xl max-h-[88vh] overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div className="flex items-center gap-2 font-display font-bold">
            <Sparkles className="h-4 w-4 text-brand-amber" /> Generate Image with AI
          </div>
          <button onClick={onClose} data-testid="mag-ai-image-close" className="text-ink-muted hover:text-ink-primary"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Image Prompt</div>
              {context?.title && (
                <button onClick={suggest} disabled={!!busy} data-testid="mag-ai-image-suggest-btn"
                  className="inline-flex items-center gap-1 rounded-full border border-brand-cyan/60 text-brand-cyan px-2 py-0.5 text-[10px] font-bold">
                  {busy === "suggest" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Suggest from article
                </button>
              )}
            </div>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} data-testid="mag-ai-image-prompt"
              placeholder={busy === "suggest" ? "Reading your article…" : "Describe the scene — e.g. a smoky late-night studio with a vintage microphone glowing under neon light"}
              className="w-full rounded-md border border-line bg-bg-elevated px-2.5 py-2 text-sm" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Style</div>
            <div className="flex gap-2">
              {STYLES.map(([k, label]) => (
                <button key={k} onClick={() => setStyle(k)} data-testid={`mag-ai-image-style-${k}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold border ${style === k ? "bg-brand-amber text-black border-brand-amber" : "border-line text-ink-secondary"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {result ? (
            <div className="space-y-2">
              <img src={result.file_url} alt={result.alt_text} className="w-full rounded-md border border-line" data-testid="mag-ai-image-result" />
              <div className="flex gap-2">
                <button onClick={() => { onUse(result); onClose(); }} data-testid="mag-ai-image-use-btn"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-amber text-black px-4 py-2 text-xs font-bold">
                  <Check className="h-3.5 w-3.5" /> Use this image
                </button>
                <button onClick={generate} disabled={!!busy} data-testid="mag-ai-image-regen-btn"
                  className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-xs font-bold">
                  {busy === "generate" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Regenerate
                </button>
              </div>
            </div>
          ) : (
            <button onClick={generate} disabled={!!busy} data-testid="mag-ai-image-generate-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-amber text-black px-4 py-2.5 text-sm font-bold disabled:opacity-60">
              {busy === "generate" ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating your image… (~30s)</> : <><Sparkles className="h-4 w-4" /> Generate Image</>}
            </button>
          )}
          <div className="text-[10px] text-ink-muted">Generated images are saved to your Media Library automatically.</div>
        </div>
      </div>
    </div>
  );
};
